import React, { useCallback } from 'react'
import { type FormikHelpers } from 'formik'
import { v4 as uuidv4 } from 'uuid'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'
import * as Yup from 'yup'
import { ValidationError } from 'yup'

import { TtButton, TtIcon } from '@23/react'

import TtFormik from '../../../../../../global/formik-forms'
import {
  useCreatePollMutation,
  useSetPollOptionsMutation,
  useUpdatePollTitleMutation,
} from '../../../hooks/use-polls'
import { getLiveId } from '../../../../../../global/blocs/roomInfo'
import { updatePollCard } from '../../../../../../firebase/hooks/useTray'
import { type Poll } from '../../../models/poll'
import { logMessage } from '../../../../../../Logging/log'

import './PollForm.scss'

type PollOption = {
  id: string
  value: string
}

type PollFormValues = {
  title: string
  options: PollOption[]
}

const validationSchema = Yup.object({
  title: Yup.string().required(() =>
    intl.formatMessage({
      defaultMessage: 'Your poll needs a question',
    })
  ),
  options: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        value: Yup.string().trim(),
      })
    )
    .test('at-least-two', (opts, ctx) => {
      const list = opts ?? []
      const emptyPaths = list
        .map((opt, idx) =>
          !opt.value?.trim() ? `options[${idx}].value` : null
        )
        .filter(Boolean) as string[]
      const filled = list.length - emptyPaths.length

      if (filled >= 2) return true

      return new ValidationError(
        emptyPaths.map((path) =>
          ctx.createError({
            path,
            message: () =>
              intl.formatMessage({
                defaultMessage: 'Your poll needs at least 2 options',
              }),
          })
        )
      )
    }),
})

interface PollFormProps {
  cardId: string
  poll?: Poll
  onSubmit?: () => void
  onCancel?: () => void
}

const PollForm = ({ cardId, poll, onSubmit, onCancel }: PollFormProps) => {
  const isEditing = poll !== undefined

  const { mutateAsync: createPoll } = useCreatePollMutation(getLiveId())
  const { mutateAsync: addPollOptions } = useSetPollOptionsMutation()

  const { mutateAsync: updatePollTitle } = useUpdatePollTitleMutation()

  const handleSubmit = useCallback(
    async (
      values: PollFormValues,
      formikHelpers: FormikHelpers<PollFormValues>
    ) => {
      try {
        let pollId = poll?.id
        if (!pollId) {
          const response = await createPoll(values.title)
          pollId = response.poll_id
        } else {
          await updatePollTitle({
            pollId,
            title: values.title,
          })
        }

        const options = values.options.filter((option) => !!option.value.trim())

        await addPollOptions({
          pollId,
          options: options.map((option) => option.value),
        })

        // Add to firebase
        updatePollCard(cardId, {
          id: pollId,
          question: values.title,
          started: false,
          startedAt: '',
          options: options.map((option, index) => ({
            id: -index,
            option: option.value,
            count: 0,
          })),
        })

        onSubmit?.()
      } catch (e) {
        logMessage('There was an error while creating or updating the poll', {
          level: 'error',
          extra: {
            isEditing,
            reason: e,
          },
        })

        formikHelpers.setFieldError(
          'default',
          isEditing
            ? intl.formatMessage({
                defaultMessage:
                  'There was an error saving the poll. Please try again.',
              })
            : intl.formatMessage({
                defaultMessage:
                  'There was an error creating the poll. Please try again.',
              })
        )
      }
    },
    [
      addPollOptions,
      cardId,
      createPoll,
      isEditing,
      onSubmit,
      poll?.id,
      updatePollTitle,
    ]
  )

  return (
    <TtFormik.Form<PollFormValues>
      className="new-poll-form"
      label={
        isEditing
          ? intl.formatMessage({ defaultMessage: 'Edit Poll Form' })
          : intl.formatMessage({ defaultMessage: 'Create Poll Form' })
      }
      initialValues={{
        title: poll?.question ?? '',
        options: poll?.options.map((option) => ({
          id: option.id.toString(),
          value: option.option,
        })) ?? [
          { id: uuidv4(), value: '' },
          { id: uuidv4(), value: '' },
        ],
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <>
          <TtFormik.TextArea
            className="poll-title"
            name="title"
            label={intl.formatMessage({ defaultMessage: 'Poll title' })}
            hideLabel
            placeholder={intl.formatMessage({
              defaultMessage: 'Type in your question...',
            })}
            lines={5}
          />

          {formik.values.options.map((option, index) => (
            <div className="poll-option-row" key={option.id}>
              <TtFormik.TextField
                className="poll-option"
                name={`options[${index}].value`}
                label={intl.formatMessage({ defaultMessage: 'Poll option' })}
                hideLabel
                placeholder={intl.formatMessage({
                  defaultMessage: 'Type in an option...',
                })}
                size="small"
              />
              {formik.values.options.length > 2 && (
                <TtButton
                  className="poll-option-remove"
                  size="small"
                  variant="outline"
                  color="secondary"
                  circle
                  onClick={() => {
                    const nextOptions = formik.values.options.filter(
                      (opt) => opt.id !== option.id
                    )
                    void formik.setFieldValue('options', nextOptions)
                  }}
                >
                  <TtIcon library="one" name="close" />
                </TtButton>
              )}
            </div>
          ))}

          {formik.values.options.length < 7 && (
            <TtButton
              size="small"
              variant="outline"
              color="secondary"
              onClick={() =>
                formik.setFieldValue('options', [
                  ...formik.values.options,
                  { id: uuidv4(), value: '' },
                ])
              }
            >
              <FormattedMessage defaultMessage="Add option" />
            </TtButton>
          )}
          <TtFormik.SubmitButton
            className="create-poll-submit"
            size="small"
            label={
              isEditing
                ? intl.formatMessage({ defaultMessage: 'Save' })
                : intl.formatMessage({ defaultMessage: 'Create Poll' })
            }
            pill
            color="secondary"
            variant="default"
            onCancel={isEditing ? onCancel : undefined}
          >
            {isEditing ? (
              <FormattedMessage defaultMessage="Save" />
            ) : (
              <FormattedMessage defaultMessage="Create Poll" />
            )}
          </TtFormik.SubmitButton>
        </>
      )}
    </TtFormik.Form>
  )
}

export { PollForm }
